import { useMemo, useState, type ChangeEvent } from "react";

import { updateAppointment } from "../redux/slices/appointmentSlice";
import { customerLookup, serviceLookup } from "../utils";
import type { AppDispatch } from "../redux/store";
import type { AppointmentState, CustomerInfo } from "../types";
import {
  validateCityState,
  validateEmail,
  validateName,
  validateZip,
} from "../utils/validation";

type CustomerFieldName = "fname" | "lname" | "email";
type AddressFieldName = "street" | "city" | "state" | "zip";
type VehicleSelectionFieldName = CustomerFieldName | AddressFieldName;

type VehicleSelectionField = {
  type: string;
  name: VehicleSelectionFieldName;
  required?: boolean;
  placeholder: string;
  label: string;
};

type VehicleSelectionCustomer = Partial<CustomerInfo> & {
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

type ExistingCustomerLookup = VehicleSelectionCustomer & {
  _id?: string;
  integrationId?: string;
  address1?: string;
  vehicles?: Array<Record<string, unknown>>;
};

type DeferredServiceLineItem = {
  description?: string;
  [key: string]: unknown;
};

type VehicleSelectionConfig = {
  domain?: string;
  shopId?: string;
};

type VehicleSelectionCustomerFormArgs = {
  appointment: AppointmentState;
  dispatch: AppDispatch;
};

const BASE_FORM_FIELDS: VehicleSelectionField[] = [
  {
    type: "text",
    name: "fname",
    required: true,
    placeholder: "First Name",
    label: "First Name",
  },
  {
    type: "text",
    name: "lname",
    required: true,
    placeholder: "Last Name",
    label: "Last Name",
  },
  {
    type: "text",
    name: "email",
    placeholder: "Email Address",
    label: "Email Address",
  },
];

const BASE_ADDRESS_FIELDS: VehicleSelectionField[] = [
  {
    type: "text",
    name: "street",
    placeholder: "Street",
    label: "Street",
  },
  {
    type: "text",
    name: "city",
    placeholder: "City",
    label: "City",
  },
  {
    type: "text",
    name: "state",
    placeholder: "State",
    label: "State",
  },
  {
    type: "text",
    name: "zip",
    placeholder: "Zip Code",
    label: "Zip Code",
  },
];

type FieldValidationFlags = {
  emailRequired: boolean;
  addressRequired: boolean;
};

// Single source of truth for per-field error messages, shared by the on-change
// handlers and the on-load seeding so a field reads the same either way.
const getFieldErrorMessage = (
  fieldName: VehicleSelectionFieldName,
  value: string,
  { emailRequired, addressRequired }: FieldValidationFlags
): string | undefined => {
  const trimmed = (value || "").trim();

  switch (fieldName) {
    case "fname":
      if (!trimmed) return "First Name is required";
      if (!validateName(value)) return "Name should not contain numbers";
      return undefined;
    case "lname":
      if (!trimmed) return "Last Name is required";
      if (!validateName(value)) return "Name should not contain numbers";
      return undefined;
    case "email":
      if (emailRequired && !trimmed) return "Email Address is required";
      if (trimmed && !validateEmail(value))
        return "Please enter a valid email address";
      return undefined;
    case "street":
      if (addressRequired && !trimmed) return "Street is required";
      return undefined;
    case "city":
      if (addressRequired && !trimmed) return "City is required";
      if (trimmed && !validateCityState(value))
        return "City should not contain numbers";
      return undefined;
    case "state":
      if (addressRequired && !trimmed) return "State is required";
      if (trimmed && !validateCityState(value))
        return "State should not contain numbers";
      return undefined;
    case "zip":
      if (addressRequired && !trimmed) return "Zip Code is required";
      if (trimmed && !validateZip(value))
        return "Zip Code contains invalid characters";
      return undefined;
    default:
      return undefined;
  }
};

const ADDRESS_FIELD_NAMES: AddressFieldName[] = [
  "street",
  "city",
  "state",
  "zip",
];

export const getRequiredVehicleSelectionFields = (
  shopData: Partial<NonNullable<AppointmentState["shopData"]>> = {}
): VehicleSelectionFieldName[] => {
  const requiredFields: VehicleSelectionFieldName[] = ["fname", "lname"];

  if (shopData?.requireAddress) {
    requiredFields.push("street", "city", "state", "zip");
  }

  if (shopData?.requireNewCustomerEmail) {
    requiredFields.push("email");
  }

  return requiredFields;
};

export const lookupExistingCustomer = async (
  phone: string = "",
  config: VehicleSelectionConfig
): Promise<ExistingCustomerLookup | null> => {
  try {
    const cleanedNumber = phone.replace(/^(\+?1)/, "");
    const data = (await customerLookup(
      cleanedNumber,
      config
    )) as ExistingCustomerLookup | null;

    return data?._id ? data : null;
  } catch (error) {
    console.error("Error posting booking config:", error);
    return null;
  }
};

export const fetchDeferredVehicleServices = async (
  customer: { _id?: string; vehicle_id?: unknown },
  config: VehicleSelectionConfig
): Promise<DeferredServiceLineItem[]> => {
  const data = (await serviceLookup(customer, config)) as {
    detail?: {
      lineItems?: DeferredServiceLineItem[];
    };
  };

  if (data?.detail?.lineItems?.length) {
    return data.detail.lineItems;
  }

  return [];
};

export const useVehicleSelectionCustomerForm = ({
  appointment,
  dispatch,
}: VehicleSelectionCustomerFormArgs) => {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [inputClicked, setInputClicked] = useState<Record<string, boolean>>({});
  const [valueCheck, setValueCheck] = useState<VehicleSelectionFieldName[]>(
    getRequiredVehicleSelectionFields(appointment?.shopData)
  );
  const [customer, setCustomer] = useState<VehicleSelectionCustomer>({});

  const emailRequired = Boolean(
    appointment?.shopData?.requireNewCustomerEmail ||
      appointment?.requireValidCustomerEmail
  );
  const addressRequired = Boolean(appointment?.shopData?.requireAddress);
  const validationFlags: FieldValidationFlags = {
    emailRequired,
    addressRequired,
  };

  const formFields = useMemo<VehicleSelectionField[]>(
    () =>
      BASE_FORM_FIELDS.map((field) =>
        field.name === "email"
          ? {
              ...field,
              required: emailRequired,
            }
          : field
      ),
    [emailRequired]
  );

  const addressFields = useMemo<VehicleSelectionField[]>(
    () =>
      BASE_ADDRESS_FIELDS.map((field) => ({
        ...field,
        required: appointment?.shopData?.requireAddress || false,
      })),
    [appointment?.shopData?.requireAddress]
  );

  const updateFieldError = (
    fieldName: VehicleSelectionFieldName,
    errorMessage?: string
  ) => {
    setFieldErrors((previousErrors) => {
      if (!errorMessage) {
        const nextErrors = { ...previousErrors };
        delete nextErrors[fieldName];
        return nextErrors;
      }

      return {
        ...previousErrors,
        [fieldName]: errorMessage,
      };
    });
  };

  const buildCustomerState = (): VehicleSelectionCustomer => ({
    ...(appointment?.customer || {}),
    ...(customer || {}),
    address: {
      ...(appointment?.customer?.address || {}),
      ...(customer?.address || {}),
    },
  });

  const syncCustomer = (
    nextCustomer: VehicleSelectionCustomer,
    editedField?: VehicleSelectionFieldName
  ) => {
    setCustomer(nextCustomer);

    const updates: Partial<AppointmentState> = {
      customer: nextCustomer as CustomerInfo,
    };

    // Once the customer edits a field, it is no longer a trusted stored value —
    // drop it from prefilledCustomerFields so the input-format regex applies.
    const prefilled = appointment?.prefilledCustomerFields || [];
    if (editedField && prefilled.includes(editedField)) {
      updates.prefilledCustomerFields = prefilled.filter(
        (field) => field !== editedField
      );
    }

    dispatch(updateAppointment({ updates }));
  };

  const updateCustomerInfo = (
    fieldName: CustomerFieldName,
    fieldValue: string
  ) => {
    const nextCustomer = {
      ...buildCustomerState(),
      [fieldName]: fieldValue,
    };

    syncCustomer(nextCustomer, fieldName);

    updateFieldError(
      fieldName,
      getFieldErrorMessage(fieldName, fieldValue, validationFlags)
    );
  };

  const updateCustomerAddress = (
    fieldName: AddressFieldName,
    fieldValue: string
  ) => {
    const nextCustomer = buildCustomerState();
    const nextAddress = {
      ...nextCustomer.address,
      [fieldName]: fieldValue,
    };

    syncCustomer(
      {
        ...nextCustomer,
        address: nextAddress,
      },
      fieldName
    );

    updateFieldError(
      fieldName,
      getFieldErrorMessage(fieldName, fieldValue, validationFlags)
    );
  };

  // Seed validation errors and mark fields as touched so problems are visible
  // immediately on load (red border + inline message) instead of only after the
  // customer focuses/edits each field.
  const flagFieldErrors = (
    fieldNames: VehicleSelectionFieldName[],
    customerData: VehicleSelectionCustomer
  ) => {
    const nextErrors: Record<string, string> = {};
    const nextTouched: Record<string, boolean> = {};

    fieldNames.forEach((fieldName) => {
      const value = ADDRESS_FIELD_NAMES.includes(fieldName as AddressFieldName)
        ? customerData?.address?.[fieldName as AddressFieldName] || ""
        : (customerData as Record<string, unknown>)?.[fieldName] || "";
      // A field only reaches this seed list because it must be addressed. An
      // invalid stored email is cleared to "" before seeding, and emailRequired
      // (driven partly by the async requireValidCustomerEmail flag) may still be
      // false here — so without forcing it, a shop that doesn't require email
      // would seed no error and the field would render with none of the
      // alert/border the required case shows. Treat a flagged email as required
      // so the invalid-email treatment is identical regardless of shop config.
      const seedFlags: FieldValidationFlags =
        fieldName === "email"
          ? { ...validationFlags, emailRequired: true }
          : validationFlags;
      const message = getFieldErrorMessage(
        fieldName,
        String(value),
        seedFlags
      );
      if (message) {
        nextErrors[fieldName] = message;
        nextTouched[fieldName] = true;
      }
    });

    setFieldErrors((previousErrors) => ({ ...previousErrors, ...nextErrors }));
    setInputClicked((previousClicked) => ({
      ...previousClicked,
      ...nextTouched,
    }));
  };

  const handleFieldChange = (
    e: ChangeEvent<HTMLInputElement>,
    isRequired?: boolean
  ) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();

    if (["street", "city", "state", "zip"].includes(name)) {
      updateCustomerAddress(name as AddressFieldName, value);
    } else {
      updateCustomerInfo(name as CustomerFieldName, value);
    }

    setValueCheck((previousValues = []) => {
      if (!isRequired) {
        return previousValues;
      }

      if (trimmedValue !== "") {
        return previousValues.filter((item) => item !== name);
      }

      if (previousValues.includes(name as VehicleSelectionFieldName)) {
        return previousValues;
      }

      return [...previousValues, name as VehicleSelectionFieldName];
    });
  };

  return {
    addressFields,
    customer,
    fieldErrors,
    flagFieldErrors,
    formFields,
    handleFieldChange,
    handleFocus: (fieldName: VehicleSelectionFieldName) =>
      setInputClicked((previousClicked) => ({
        ...previousClicked,
        [fieldName]: true,
      })),
    hasFieldError: (fieldName: VehicleSelectionFieldName) =>
      Boolean(inputClicked[fieldName] && valueCheck.includes(fieldName)),
    setCustomer,
    setValueCheck,
    valueCheck,
  };
};
